import { Phone, Eye, Printer } from 'lucide-react';
import { Order } from '../types';
import { ResponsiveTable, Column } from '../../../components/ui/ResponsiveTable';
import { StatusBadge } from './StatusBadge';
import { PaymentBadge } from './PaymentBadge';
import { formatThaiDateTime } from '../../../utils/dateUtils';
import { cn } from '../../../utils/responsive';

interface OrderTableProps {
    orders: Order[];
    isBatchMode: boolean;
    selectedOrderIds: Set<string>;
    onSelectOrder: (orderId: string | number) => void;
    onSelectAll: (selected: boolean) => void;
    onViewDetails: (order: Order) => void;
    onPrintLabel: (order: Order) => void;
    isPrinting: boolean;
    emptyMessage?: string;
}

export function OrderTable({
    orders,
    isBatchMode,
    selectedOrderIds,
    onSelectOrder,
    onSelectAll,
    onViewDetails,
    onPrintLabel,
    isPrinting,
    emptyMessage = 'ไม่มีคำสั่งซื้อ',
}: OrderTableProps) {
    const columns: Column<Order>[] = [
        {
            key: 'orderNumber',
            header: 'เลขที่คำสั่งซื้อ',
            render: (order) => (
                <div className="font-mono text-sm font-medium text-stone-900 dark:text-white">
                    #{order.orderNumber}
                </div>
            ),
            className: 'w-36',
        },
        {
            key: 'customer',
            header: 'ลูกค้า',
            render: (order) => (
                <div>
                    <div className="text-sm font-medium text-stone-900 dark:text-white">
                        {order.customerName}
                    </div>
                    <div className="text-xs text-stone-500 dark:text-stone-400 flex items-center gap-1 mt-0.5">
                        <Phone className="w-3 h-3" />
                        <span className="font-mono">{order.customerPhone}</span>
                    </div>
                </div>
            ),
        },
        {
            key: 'quantity',
            header: 'จำนวนสินค้า',
            render: (order) => (
                <div className="text-sm text-stone-900 dark:text-white text-center font-mono">
                    {order.items.reduce((sum, item) => sum + item.quantity, 0)} ชิ้น
                </div>
            ),
            className: 'w-28 text-center',
            mobileHidden: true,
        },
        {
            key: 'total',
            header: 'ยอดรวม',
            render: (order) => (
                <div>
                    <div className="text-sm font-mono font-bold text-emerald-600">
                        ฿{order.totalAmount.toLocaleString('th-TH')}
                    </div>
                    <div className="mt-1">
                        <PaymentBadge method={order.paymentMethod} />
                    </div>
                </div>
            ),
            className: 'w-36',
        },
        {
            key: 'time',
            header: 'เวลา',
            render: (order) => (
                <div className="text-xs font-mono text-stone-500 dark:text-stone-400">
                    {formatThaiDateTime(order.createdAt)}
                </div>
            ),
            className: 'w-36',
            mobileHidden: true,
        },
        {
            key: 'actions',
            header: 'การจัดการ',
            render: (order) => (
                <div className="flex items-center justify-end gap-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewDetails(order);
                        }}
                        className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-gray-700 active:scale-95 transition-all touch-target"
                        title="ดูรายละเอียด"
                    >
                        <Eye className="w-4 h-4 text-yellow-600 dark:text-yellow-500" />
                    </button>

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onPrintLabel(order);
                        }}
                        disabled={isPrinting}
                        className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-gray-700 active:scale-95 transition-all touch-target disabled:opacity-50"
                        title={order.status === 'paid' ? 'ปริ้นใบขนส่ง' : 'ปริ้นใบขนส่งอีกครั้ง'}
                    >
                        <Printer className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                    </button>
                </div>
            ),
            className: 'w-28 text-right',
        },
    ];

    const renderOrderCard = (order: Order) => {
        const isSelected = selectedOrderIds.has(order.id);

        return (
            <div
                key={order.id}
                className={cn(
                    'group bg-white dark:bg-gray-800 rounded-lg border p-4',
                    'transition-all duration-200',
                    'hover:border-yellow-400 hover:shadow-accent',
                    isSelected ? 'border-yellow-400 bg-yellow-50/30 dark:bg-yellow-900/20' : 'border-stone-200 dark:border-gray-700'
                )}
            >
                <div className="flex items-start gap-3 mb-3">
                    <div className="flex-1 flex justify-between items-start">
                        <div>
                            <span className="font-mono text-xs text-stone-500 dark:text-gray-400">
                                #{order.orderNumber}
                            </span>
                            <p className="text-sm font-medium text-stone-900 dark:text-gray-100 mt-0.5">
                                {order.customerName}
                            </p>
                        </div>
                        <StatusBadge status={order.status} />
                    </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-stone-600 dark:text-gray-400 mb-3">
                    <Phone className="w-4 h-4" />
                    <span className="font-mono">{order.customerPhone}</span>
                </div>

                <div className="mb-4">
                    <span className="text-2xl font-mono font-bold text-emerald-600 dark:text-emerald-400">
                        ฿{order.totalAmount.toLocaleString('th-TH')}
                    </span>
                    <div className="mt-2">
                        <PaymentBadge method={order.paymentMethod} />
                    </div>
                </div>

                <div className="flex items-center justify-between text-xs text-stone-500 dark:text-gray-400 mb-4">
                    <span className="font-mono">{formatThaiDateTime(order.createdAt)}</span>
                    <span className="font-mono">{order.items.reduce((sum, item) => sum + item.quantity, 0)} ชิ้น</span>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={() => onPrintLabel(order)}
                        disabled={isPrinting}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5
                     bg-gradient-to-r from-yellow-400 to-orange-500 text-white
                     rounded-lg font-medium shadow-sm
                     hover:shadow-accent hover:scale-[1.02]
                     active:scale-[0.98]
                     disabled:opacity-50
                     transition-all duration-200 touch-target"
                    >
                        <Printer className="w-4 h-4" />
                        พิมพ์ใบปะหน้า
                    </button>
                    <button
                        onClick={() => onViewDetails(order)}
                        className="px-4 py-2.5 bg-stone-100 dark:bg-gray-700 text-stone-700 dark:text-gray-200 rounded-lg
                     hover:bg-stone-200 dark:hover:bg-gray-600 active:scale-[0.98]
                     transition-all duration-200 touch-target"
                    >
                        <Eye className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <ResponsiveTable
            data={orders}
            columns={columns}
            renderCard={renderOrderCard}
            getItemId={(order) => order.id}
            selectable={isBatchMode}
            selectedIds={selectedOrderIds}
            onSelect={onSelectOrder}
            onSelectAll={onSelectAll}
            emptyMessage={emptyMessage}
        />
    );
}
